import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import {
  dashRetry,
  gaitAllowed,
  knockStart,
  knockStep,
  type GaitKind,
  type KnockState,
} from '@/domain/hs1'
import { createDashStore, type DashStore } from './store'

/**
 * Der Rahmen des Easter Eggs — was Bühne und Anzeige gemeinsam brauchen.
 *
 * WARUM ES EINEN PROVIDER BRAUCHT UND KEINEN ZUSTAND IN EINER KOMPONENTE. Das
 * Spiel hat zwei Hälften an entgegengesetzten Enden des Baums: die Welt lebt
 * IM Canvas (und damit in einem lazy geladenen Bündel), die Anzeige DARÜBER im
 * DOM. Beide brauchen denselben Zustand, und keine ist Elternteil der anderen.
 *
 * DER AUSLÖSER LIEGT AUCH HIER, weil er zu keiner der beiden Hälften gehört: er
 * hängt an der Wortmarke in der Kopfleiste, die vom Spiel nichts weiss und auch
 * nichts wissen soll.
 */

export interface DashContext {
  store: DashStore
  running: boolean
  /** Die laufende Gangart. Nur `walk`, `run`, `sprint`. */
  gait: GaitKind
  /** Angehalten, solange die Legende offen ist. */
  legend: boolean
  setLegend: (open: boolean) => void
  setGait: (gait: GaitKind) => void
  /** Wird beim nächsten Schritt gelesen und geleert. */
  jump: React.MutableRefObject<boolean>
  /** Losgelaufen — beendet das Warten an der Startlinie. */
  go: React.MutableRefObject<boolean>
  /** Aufstehen und an der Startlinie neu beginnen. */
  retry: () => void
  /** Die Höhe der Figur über dem Boden, m — die Bühne schreibt, die Figur liest. */
  lift: React.MutableRefObject<number>
  /** Der Sturz, 0 … 1. */
  fall: React.MutableRefObject<number>
  requestJump: () => void
  start: () => void
  restart: () => void
  stop: () => void
  /** Ein Klick auf die Wortmarke. Gibt zurück, was zu zeigen ist. */
  knock: () => { remaining: number; hint: boolean }
}

const Ctx = createContext<DashContext | null>(null)

export function DashProvider({ children }: { children: ReactNode }) {
  const store = useMemo(createDashStore, [])
  /*
    `running` WIRD GELESEN UND NICHT GEHALTEN. Im ersten Entwurf stand es
    zweimal: als Flag hier und als Feld im Zustand. Beim ersten Versuch, das
    Spiel ohne den Auslöser zu starten, liefen die beiden auseinander — die Bühne
    hielt das Spiel für laufend, die Anzeige nicht, und beide hatten recht.
    Genau derselbe Fehler, den zwei Rigs oder zwei Uhren machen.
  */
  const running = useSyncExternalStore(store.subscribe, () => store.get().running)
  const [gait, setGaitState] = useState<GaitKind>('run')
  const [legend, setLegend] = useState(false)
  const jump = useRef(false)
  const go = useRef(false)
  const lift = useRef(0)
  const fall = useRef(0)
  /* Die Regel selbst steht im Kern und hat dort Tests — hier liegt nur ihr
     Zustand. */
  const knocks = useRef<KnockState>(knockStart())

  const start = useCallback(() => {
    store.begin()
    setGaitState('run')
    setLegend(false)
  }, [store])

  const restart = useCallback(() => {
    store.restart()
    setGaitState('run')
    setLegend(false)
  }, [store])

  /* Aufstehen nach einem Sturz — der nächste Versuch, nicht ein neues Spiel:
     die Hüllen und der Akku bleiben, wie sie sind. */
  const retry = useCallback(() => {
    store.set(dashRetry(store.get()))
  }, [store])

  const stop = useCallback(() => {
    store.stop()
    setLegend(false)
    lift.current = 0
    fall.current = 0
  }, [store])

  const setGait = useCallback(
    (next: GaitKind) => {
      /* Die Sperre bei Übertemperatur steht im Kern. Hier wird sie nur befolgt —
         zwei Stellen, die dieselbe Regel kennen, sind irgendwann uneins. */
      if (!gaitAllowed(store.get(), next)) return
      go.current = true
      setGaitState(next)
    },
    [store],
  )

  const requestJump = useCallback(() => {
    jump.current = true
  }, [])

  /* Jede Bedienung ist zugleich ein Startsignal: wer an der Startlinie eine
     Gangart wählt, will loslaufen und nicht erst noch eine zweite Taste
     suchen. */
  const requestGo = useCallback(() => {
    go.current = true
  }, [])

  const knock = useCallback(() => {
    const result = knockStep(knocks.current, performance.now())
    knocks.current = result.state
    if (result.open) start()
    return { remaining: result.remaining, hint: result.hint }
  }, [start])

  /*
    ÜBERTEMPERATUR ERZWINGT DAS GEHEN, und zwar hier und nicht im Kern: der Kern
    kennt den Zustand, aber die Gangart ist eine Eingabe von aussen — er kann sie
    nicht selbst umstellen, ohne seine eigene Richtung umzukehren.
  */
  useEffect(() => {
    if (!running) return
    return store.subscribe(() => {
      if (store.get().hot) setGaitState((g) => (g === 'walk' ? g : 'walk'))
    })
  }, [running, store])

  /*
    TASTEN AM DOKUMENT UND NICHT AM FENSTER, und die Weitergabe wird gestoppt.
    Die Anwendung hört selbst auf Tasten; ohne das Anhalten schaltete die
    Leertaste im Spiel die Wiedergabe der Simulation ab.
  */
  useEffect(() => {
    if (!running) return
    const onKey = (e: KeyboardEvent) => {
      const mine =
        e.code === 'Space' ||
        e.code === 'ArrowUp' ||
        e.code === 'Digit1' ||
        e.code === 'Digit2' ||
        e.code === 'Digit3' ||
        e.code === 'KeyI' ||
        e.key === 'Escape'
      if (!mine) return
      e.preventDefault()
      e.stopPropagation()

      if (e.key === 'Escape') {
        if (legend) setLegend(false)
        else stop()
        return
      }
      if (e.code === 'KeyI') {
        setLegend((v) => !v)
        return
      }
      if (legend) return
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        requestJump()
        requestGo()
        return
      }
      if (e.code === 'Digit1') setGait('walk')
      if (e.code === 'Digit2') setGait('run')
      if (e.code === 'Digit3') setGait('sprint')
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [running, legend, stop, setGait, requestJump, requestGo])

  const value = useMemo<DashContext>(
    () => ({
      store,
      running,
      gait,
      legend,
      setLegend,
      setGait,
      jump,
      go,
      lift,
      fall,
      requestJump,
      retry,
      start,
      restart,
      stop,
      knock,
    }),
    [store, running, gait, legend, setGait, requestJump, retry, start, restart, stop, knock],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useDash(): DashContext {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useDash ausserhalb von <DashProvider>')
  return ctx
}
